import type { Connector, State } from '@particle-network/connector-core';
import { checksumAddress, isAddress, type Address, type Chain } from 'viem';
import type { Config } from '../createConfig';
import { deepEqual } from '../utils/deepEqual';

export type GetAccountReturnType =
  | {
      address: string;
      addresses: readonly [string, ...string[]];
      chain: Chain | undefined;
      chainId: number;
      connector: Connector;
      isConnected: true;
      isConnecting: false;
      isDisconnected: false;
      isReconnecting: false;
      status: 'connected';
    }
  | {
      address: string | undefined;
      addresses: readonly string[] | undefined;
      chain: Chain | undefined;
      chainId: number | undefined;
      connector: Connector | undefined;
      isConnected: boolean;
      isConnecting: false;
      isDisconnected: false;
      isReconnecting: true;
      status: 'reconnecting';
    }
  | {
      address: string | undefined;
      addresses: readonly string[] | undefined;
      chain: Chain | undefined;
      chainId: number | undefined;
      connector: Connector | undefined;
      isConnected: false;
      isReconnecting: false;
      isConnecting: true;
      isDisconnected: false;
      status: 'connecting';
    }
  | {
      address: undefined;
      addresses: undefined;
      chain: undefined;
      chainId: undefined;
      connector: undefined;
      isConnected: false;
      isReconnecting: false;
      isConnecting: false;
      isDisconnected: true;
      status: 'disconnected';
    };

let previousAccount: GetAccountReturnType;
let previousState: State;
export function getAccount(config: Config): GetAccountReturnType {
  const state = config.state;
  if (deepEqual(previousState, state)) return previousAccount;

  const uid = config.state.current!;
  const connection = config.state.connections.get(uid);
  let addresses = connection?.accounts;
  if (addresses?.length && addresses.every((account) => isAddress(account, { strict: false }))) {
    addresses = addresses.map((item) => checksumAddress(item as Address)) as unknown as readonly [string, ...string[]];
  }
  const address = addresses?.[0];
  const chain = config.chains.find((chain) => chain.id === connection?.chainId) as GetAccountReturnType['chain'];
  const status = config.state.status;

  let result;

  switch (status) {
    case 'connected':
      result = {
        address: address!,
        addresses: addresses!,
        chain,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        chainId: connection?.chainId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        connector: connection?.connector!,
        isConnected: true,
        isConnecting: false,
        isDisconnected: false,
        isReconnecting: false,
        status,
      };
      break;
    case 'reconnecting':
      result = {
        address,
        addresses,
        chain,
        chainId: connection?.chainId,
        connector: connection?.connector,
        isConnected: !!address,
        isConnecting: false,
        isDisconnected: false,
        isReconnecting: true,
        status,
      };
      break;
    case 'connecting':
      result = {
        address,
        addresses,
        chain,
        chainId: connection?.chainId,
        connector: connection?.connector,
        isConnected: false,
        isConnecting: true,
        isDisconnected: false,
        isReconnecting: false,
        status,
      };
      break;
    case 'disconnected':
      result = {
        address: undefined,
        addresses: undefined,
        chain: undefined,
        chainId: undefined,
        connector: undefined,
        isConnected: false,
        isConnecting: false,
        isDisconnected: true,
        isReconnecting: false,
        status,
      };
      break;
  }
  previousState = state;
  previousAccount = result;
  return result;
}
